import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PublicPlansComparison } from '@/components/landing/PublicPlansComparison';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { ScrollToTopButton } from '@/components/landing/ScrollToTopButton';

type BillingPeriod = 'monthly' | 'annual';

export default function PricingDetails() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  const { data: plans, isLoading } = useQuery({
    queryKey: ['public_plans_detailed'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const getPrice = (plan: any) => {
    return billingPeriod === 'monthly' ? plan.price_monthly : plan.price_annual;
  };

  const getPriceLabel = () => {
    return billingPeriod === 'monthly' ? '/mês' : '/ano';
  };

  const handleSelectPlan = (planId: string) => {
    navigate(`/auth?signup=true&plan=${planId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Carregando planos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
          <h1 className="text-4xl md:text-5xl font-bold">
            Compare todos os planos
          </h1>
          <p className="text-xl text-muted-foreground">
            Veja em detalhes todas as funcionalidades incluídas em cada plano
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <Tabs value={billingPeriod} onValueChange={(value) => setBillingPeriod(value as BillingPeriod)}>
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="monthly">Mensal</TabsTrigger>
              <TabsTrigger value="annual">
                Anual
                <Badge variant="secondary" className="ml-2">-20%</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Price Cards Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-12">
          {plans?.map((plan) => {
            const price = getPrice(plan);
            
            return (
              <div key={plan.id} className="bg-card border rounded-lg p-6 space-y-4">
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <div className="space-y-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">
                      R$ {price?.toFixed(2)}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {getPriceLabel()}
                    </span>
                  </div>
                  {billingPeriod === 'annual' && plan.price_monthly > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Economize R$ {((plan.price_monthly * 12) - plan.price_annual).toFixed(2)}/ano
                    </p>
                  )}
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  Escolher Plano
                </Button>
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        {plans && <PublicPlansComparison plans={plans} />}

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto text-center space-y-6 mt-16 p-8 bg-muted/50 rounded-lg">
          <h2 className="text-2xl md:text-3xl font-bold">
            Pronto para começar?
          </h2>
          <p className="text-muted-foreground">
            Escolha o plano ideal para o seu negócio e comece a transformar seus dados em decisões estratégicas
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/auth?signup=true')}
            className="gap-2"
          >
            <TrendingUp className="h-5 w-5" />
            Começar Agora
          </Button>
        </div>
      </div>

      <LandingFooter />
      <ScrollToTopButton />
    </div>
  );
}
